import React, { useEffect, useState } from "react";
import {
    getDataCorrectionLogs,
    getAllDataCorrectionLogs,
    revertCorrectionRequest,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { PERMISSIONS } from "../utils/permissions";

export default function DataCorrectionLogs({ initialRequestId }) {
    const { hasPermission } = useAuth();

    const canViewLogs =
        hasPermission(PERMISSIONS.DATA_CORRECTION_UPLOADER) ||
        hasPermission(PERMISSIONS.DATA_CORRECTION_APPROVER);

    const [requestIdInput, setRequestIdInput] = useState(initialRequestId || "");
    const [activeRequestId, setActiveRequestId] = useState(initialRequestId || "");
    const [requestMeta, setRequestMeta] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reverting, setReverting] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);
    const [showRevertModal, setShowRevertModal] = useState(false);

    useEffect(() => {
        if (initialRequestId) {
            setRequestIdInput(initialRequestId);
            handleLoad(initialRequestId);
        } else {
            handleLoad("");
        }
    }, [initialRequestId]);

    if (!canViewLogs) {
        return (
            <div style={{ padding: "30px" }}>
                You do not have access to Data Correction Logs.
            </div>
        );
    }

    const sortLogs = (logItems) => {
        return [...logItems].sort((a, b) => {
            const tA = new Date(a.change_timestamp.split("#")[0]).getTime();
            const tB = new Date(b.change_timestamp.split("#")[0]).getTime();
            return tB - tA; // Descending
        });
    };

    const handleLoad = async (idFromClick) => {
        const rawId = idFromClick ?? requestIdInput;
        const id = (rawId || "").trim();

        setMessage(null);
        setMessageType(null);
        setLoading(true);

        try {
            if (id) {
                const res = await getDataCorrectionLogs(id);
                const { request, logs: logItems } = res.data;

                setActiveRequestId(id);
                setRequestMeta(request || null);
                setLogs(sortLogs(logItems || []));

                if (!logItems || logItems.length === 0) {
                    setMessage("No logs found for this Request ID.");
                    setMessageType("info");
                } else {
                    setMessage(`Loaded ${logItems.length} log entries for this request.`);
                    setMessageType("success");
                }
            } else {
                const res = await getAllDataCorrectionLogs();
                const logItems = res.data.logs || res.data.items || [];
                setActiveRequestId("");
                setRequestMeta(null);
                setLogs(sortLogs(logItems));

                if (logItems.length === 0) {
                    setMessage("No logs found in the database.");
                    setMessageType("info");
                } else {
                    setMessage(`Loaded ${logItems.length} log entries.`);
                    setMessageType("success");
                }
            }
        } catch (err) {
            console.error("Failed to load logs", err);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to load logs.";
            setMessage(backendMsg);
            setMessageType("error");
            setRequestMeta(null);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (ts) => {
        if (!ts) return "-";
        const [iso] = String(ts).split("#");
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return ts;
        return d.toLocaleString();
    };

    const canRevert =
        hasPermission(PERMISSIONS.DATA_CORRECTION_APPROVER) &&
        !!requestMeta &&
        requestMeta.status === "APPROVED" &&
        !requestMeta.reverted;

    const handleRevertClick = () => {
        if (!activeRequestId) return;
        setShowRevertModal(true);
    };

    const confirmRevert = async () => {
        setShowRevertModal(false);
        setReverting(true);
        setMessage(null);
        setMessageType(null);

        try {
            const res = await revertCorrectionRequest(activeRequestId);
            const msg =
                res.data?.message || "Revert completed successfully for this request.";
            setMessage(msg);
            setMessageType("success");

            // Mark as reverted locally so the button disappears
            setRequestMeta((prev) =>
                prev
                    ? {
                        ...prev,
                        reverted: true,
                        reverted_at: new Date().toISOString(),
                    }
                    : prev
            );
        } catch (err) {
            console.error("Revert failed", err);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to revert this request.";
            setMessage(backendMsg);
            setMessageType("error");
        } finally {
            setReverting(false);
        }
    };

    return (
        <div style={{ padding: "30px", maxWidth: "1100px", position: "relative" }}>
            <h2 style={{ marginBottom: "10px" }}>Data Correction Logs</h2>

            {/* Banner */}
            {message && (
                <div
                    style={{
                        margin: "10px 0 20px",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                        background:
                            messageType === "success"
                                ? "#e8f8f0"
                                : messageType === "info"
                                    ? "#e5f2ff"
                                    : "#fdecea",
                        border:
                            messageType === "success"
                                ? "1px solid #c8e6d9"
                                : messageType === "info"
                                    ? "1px solid #b3d4ff"
                                    : "1px solid #f5c2c7",
                        color:
                            messageType === "success"
                                ? "#148f50"
                                : messageType === "info"
                                    ? "#1d4ed8"
                                    : "#b71c1c",
                    }}
                >
                    {message}
                </div>
            )}

            {/* Search box */}
            <div
                style={{
                    background: "#fff",
                    padding: "16px 20px",
                    borderRadius: "10px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    marginBottom: "20px",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                }}
            >
                <div style={{ flex: 1 }}>
                    <label
                        style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 600,
                            marginBottom: "6px",
                        }}
                    >
                        Request ID (leave blank to see all logs)
                    </label>
                    <input
                        type="text"
                        value={requestIdInput}
                        onChange={(e) => setRequestIdInput(e.target.value)}
                        placeholder="Paste a data correction request_id or leave empty"
                        style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            fontSize: "13px",
                        }}
                    />
                </div>
                <button
                    onClick={() => handleLoad()}
                    disabled={loading}
                    style={{
                        padding: "10px 18px",
                        borderRadius: "6px",
                        border: "none",
                        background: loading ? "#95a5a6" : "#3498db",
                        color: "#fff",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        marginTop: "20px",
                        height: "38px",
                    }}
                >
                    {loading ? "Loading..." : "Load Logs"}
                </button>
            </div>

            {/* Request metadata (only when viewing a single request) */}
            {requestMeta && activeRequestId && (
                <div
                    style={{
                        background: "#fff",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                        marginBottom: "20px",
                        fontSize: "13px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "16px",
                    }}
                >
                    <div>
                        <div style={{ marginBottom: "4px" }}>
                            <strong>Request ID:</strong> {activeRequestId}
                        </div>
                        <div style={{ marginBottom: "4px" }}>
                            <strong>File:</strong> {requestMeta.file_name}
                        </div>
                        <div style={{ marginBottom: "4px" }}>
                            <strong>Uploader:</strong> {requestMeta.user_id}
                        </div>
                        <div style={{ marginBottom: "4px" }}>
                            <strong>Status:</strong> {requestMeta.status}
                            {requestMeta.reverted && (
                                <span
                                    style={{
                                        marginLeft: 8,
                                        fontSize: 11,
                                        padding: "2px 8px",
                                        borderRadius: 999,
                                        border: "1px solid #f97316",
                                        background: "#fff7ed",
                                        color: "#c05621",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Reverted
                                </span>
                            )}
                        </div>
                        <div>
                            <strong>Uploaded at:</strong>{" "}
                            {requestMeta.uploaded_at
                                ? new Date(requestMeta.uploaded_at).toLocaleString()
                                : "-"}
                        </div>
                        {requestMeta.reverted_at && (
                            <div>
                                <strong>Reverted at:</strong>{" "}
                                {new Date(requestMeta.reverted_at).toLocaleString()}
                            </div>
                        )}
                    </div>

                    {canRevert && (
                        <button
                            onClick={handleRevertClick}
                            disabled={reverting}
                            style={{
                                padding: "8px 14px",
                                borderRadius: "999px",
                                border: "none",
                                cursor: reverting ? "not-allowed" : "pointer",
                                fontWeight: 600,
                                fontSize: "13px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                background: "#f97316",
                                color: "#fff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                            }}
                        >
                            {reverting ? "Reverting..." : "Revert"}
                        </button>
                    )}
                </div>
            )}

            {/* Logs table */}
            <div
                style={{
                    background: "#fff",
                    borderRadius: "10px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                }}
            >
                <table
                    width="100%"
                    cellPadding={8}
                    style={{ borderCollapse: "collapse", fontSize: "13px" }}
                >
                    <thead>
                        <tr style={{ background: "#f3f4f6" }}>
                            <th align="left">Timestamp</th>
                            <th align="left">Entity Type</th>
                            <th align="left">Entity ID</th>
                            <th align="left">Field</th>
                            <th align="left">Old Value</th>
                            <th align="left">New Value</th>
                            <th align="left">Reverted</th>
                            <th align="left">Reverted By</th>
                            <th align="left">Reverted At</th>
                            <th align="left">Approved By</th>

                        </tr>
                    </thead>
                    <tbody>
                        {logs.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={10}
                                    style={{
                                        padding: "14px",
                                        textAlign: "center",
                                        color: "#6b7280",
                                    }}
                                >
                                    No logs to display.
                                </td>
                            </tr>
                        )}
                        {logs.map((log, idx) => (
                            <tr
                                key={idx}
                                style={{
                                    borderTop: "1px solid #e5e7eb",
                                    background: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                                }}
                            >
                                <td>{formatTimestamp(log.change_timestamp)}</td>
                                <td>{log.entity_type || "-"}</td>
                                <td style={{ maxWidth: "160px", wordBreak: "break-all" }}>
                                    {log.entity_id || "-"}
                                </td>
                                <td>{log.field_name}</td>
                                <td style={{ maxWidth: "220px", wordBreak: "break-word" }}>
                                    {log.old_value === "" ? <em>(empty)</em> : log.old_value}
                                </td>
                                <td style={{ maxWidth: "220px", wordBreak: "break-word" }}>
                                    {log.new_value === "" ? <em>(empty)</em> : log.new_value}
                                </td>
                                <td>
                                    {log.reverted ? "true" : "false"}
                                </td>
                                <td>{log.reverted_by || "-"}</td>
                                <td>
                                    {log.reverted_at
                                        ? new Date(log.reverted_at).toLocaleString()
                                        : "-"}
                                </td>
                                <td>{log.approved_by || "-"}</td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Revert Confirmation Modal */}
            {showRevertModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: "24px",
                            borderRadius: "12px",
                            width: "400px",
                            maxWidth: "90%",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                    >
                        <h3 style={{ marginTop: 0, marginBottom: "12px" }}>Confirm Revert</h3>
                        <p style={{ fontSize: "14px", color: "#4b5563", marginBottom: "24px" }}>
                            Are you sure you want to revert all changes from this request? This will restore the original values for all affected fields.
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button
                                onClick={() => setShowRevertModal(false)}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    background: "#fff",
                                    color: "#374151",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRevert}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "6px",
                                    border: "none",
                                    background: "#dc2626",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                }}
                            >
                                Revert Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
